import { getTitleCompanyFormConfig } from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/EditDrawer.companyConfig';
import {
  getClosingFundingDatesFormConfig,
  getSchedulingFormConfig,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/EditDrawer.config';
import _ from 'lodash';

import {
  CLOSING_COMPANIES,
  CLOSING_COMPANY_FIELD_NAMES,
  CLOSING_FORM_PATH,
} from './LoanEditorCustomFormDrawer.constants';
import {
  checkIsEntityAvailable,
  flattenGroupFields,
  toDrawerEntityField,
} from './LoanEditorCustomFormDrawer.entity.utils';

const CLOSING_COMPANY_BY_PREFIX = _.keyBy(CLOSING_COMPANIES, 'prefix');

export const findClosingCompany = ({ closingCompanies, closingCompanyType }) =>
  (closingCompanies ?? []).find(company => company.closingCompanyType === closingCompanyType) ??
  null;

export const buildClosingCompaniesByPrefix = closingCompanies =>
  CLOSING_COMPANIES.reduce((acc, { prefix, closingCompanyType }) => {
    acc[prefix] = findClosingCompany({ closingCompanies, closingCompanyType });

    return acc;
  }, {});

const getSchedulingFields = closingContext =>
  flattenGroupFields(
    getSchedulingFormConfig({
      isTwoPartClosing: closingContext.isTwoPartClosing,
      isUnsecured: closingContext.isUnsecured,
      closeAt: closingContext.closeAt,
    }).groups,
  ).map(field =>
    toDrawerEntityField({
      field,
      formPath: CLOSING_FORM_PATH,
      isDisabled: !closingContext.isEditAllowed,
    }),
  );

const getClosingFundingDatesFields = closingContext =>
  flattenGroupFields(
    getClosingFundingDatesFormConfig({
      hasCloseAt: !!closingContext.closeAt,
      hasNoteDate: !!closingContext.noteDate,
      isUnsecured: closingContext.isUnsecured,
      businessDays: closingContext.businessDays,
      isRescissionApplicable: closingContext.isRescissionApplicable,
      isChattel: closingContext.isChattel,
      fundingDate: closingContext.fundingDate,
    }).groups,
  ).map(field =>
    toDrawerEntityField({
      field,
      formPath: CLOSING_FORM_PATH,
      isDisabled: !closingContext.isEditAllowed || closingContext.isLockGatedFieldsDisabled,
    }),
  );

const getClosingCompanyFields = closingContext => {
  const companies = closingContext.companies ?? {};

  return flattenGroupFields(
    getTitleCompanyFormConfig({
      titleCompanyOptions: [],
      escrowCompanyOptions: [],
      sellerAttorneyOptions: [],
      buyerAttorneyOptions: [],
      hasTitleCompany: !!companies.title,
      hasEscrowCompany: !!companies.escrow,
      hasSellerAttorney: !!companies.sellerAttorney,
      hasBuyerAttorney: !!companies.buyerAttorney,
    }).groups,
  ).map(field =>
    toDrawerEntityField({
      field,
      formPath: CLOSING_FORM_PATH,
      isDisabled: !closingContext.isEditAllowed,
    }),
  );
};

const PROBE_CLOSING_CONTEXT = { isTwoPartClosing: true, isEditAllowed: true };

export const CLOSING_INFORMATION_FIELD_NAMES = new Set(
  [
    ...getSchedulingFields(PROBE_CLOSING_CONTEXT),
    ...getClosingFundingDatesFields(PROBE_CLOSING_CONTEXT),
  ].map(({ fieldId }) => fieldId),
);

export const getClosingSeedFields = closingContext =>
  checkIsEntityAvailable(closingContext)
    ? [
        ...getSchedulingFields(closingContext),
        ...getClosingFundingDatesFields(closingContext),
        ...getClosingCompanyFields(closingContext),
      ]
    : [];

export const getClosingConfigFieldsForContext = closingContext =>
  checkIsEntityAvailable(closingContext)
    ? _.keyBy(
        [
          ...getSchedulingFields(closingContext),
          ...getClosingFundingDatesFields(closingContext),
        ].filter(field => CLOSING_INFORMATION_FIELD_NAMES.has(field.fieldId)),
        'fieldId',
      )
    : {};

export const getClosingCompanyConfigFieldsForContext = closingContext => {
  const company = CLOSING_COMPANY_BY_PREFIX[closingContext?.selectedCompanyPrefix];

  if (!checkIsEntityAvailable(closingContext) || !company) {
    return {};
  }

  const fieldsById = _.keyBy(getClosingCompanyFields(closingContext), 'fieldId');
  const fieldIds = company.getFieldIds(company.prefix);

  return [...CLOSING_COMPANY_FIELD_NAMES].reduce((acc, fieldName) => {
    const field = fieldsById[fieldIds[fieldName]];

    if (field) {
      acc[fieldName] = field;
    }

    return acc;
  }, {});
};

export const buildClosingCompanySelectorOptions = companiesByPrefix =>
  CLOSING_COMPANIES.filter(({ prefix }) => !!companiesByPrefix?.[prefix]).map(
    ({ prefix, label }) => {
      const companyName = companiesByPrefix[prefix].name;

      return { id: prefix, fullName: companyName ? `${label}: ${companyName}` : label };
    },
  );
